import { constantRouterMap } from '@/router/index';
import Layout from '@/views/layout/Layout';
import pageRegistry from '@/router/pageRegistry';

/**
 * 将后端返回的扁平菜单列表转成树（按 parentId）
 */
function buildMenuTree(flatMenus) {
  if (!flatMenus || !flatMenus.length) return [];
  const list = flatMenus.map(m => ({ ...m, children: [] }));
  const byId = {};
  list.forEach(m => { byId[m.id] = m; });
  const roots = [];
  list.forEach(m => {
    if (m.parentId == null || m.parentId === 0) {
      roots.push(m);
    } else {
      const parent = byId[m.parentId];
      if (parent && parent.children) parent.children.push(m);
      else roots.push(m);
    }
  });
  return roots;
}

function getPath(name) {
  const reg = pageRegistry[name];
  return (reg && reg.path) ? reg.path : name;
}

/** 递归收集菜单树下所有在 pageRegistry 中有组件的叶子节点（扁平） */
function collectLeafMenus(menuNode, out) {
  const reg = pageRegistry[menuNode.name];
  if (reg && reg.component) {
    out.push({
      ...menuNode,
      path: getPath(menuNode.name)
    });
    return;
  }
  (menuNode.children || []).forEach(child => collectLeafMenus(child, out));
}

/**
 * 根据后端菜单树 + 前端页面注册表，生成可访问的路由树
 * 未在 pageRegistry 中配置的节点视为父级（Layout），path 用 name
 */
function buildRoutersFromMenuTree(menuTree) {
  const routers = [];
  for (const node of menuTree) {
    if (node.name === 'home') continue; // home 已在 constantRouterMap 中
    const reg = pageRegistry[node.name];

    const meta = {
      title: node.title != null ? node.title : node.name,
      icon: node.icon != null ? node.icon : ''
    };
    const sort = node.sort != null ? node.sort : 0;
    const hidden = node.hidden != null && node.hidden !== 0;

    // 在 registry 里且有 component → 叶子（可单独作为顶级或作为子路由）
    if (reg && reg.component) {
      const seg = getPath(node.name);
      routers.push({
        path: '/' + seg,
        name: node.name,
        component: reg.component,
        meta,
        sort,
        hidden
      });
      continue;
    }

    // 未配置或无 component → 父级（Layout），path 用 name，redirect 用第一个子
    const basePath = '/' + node.name;
    const leaves = [];
    collectLeafMenus(node, leaves);
    const childRouters = leaves.map(leaf => {
      const leafReg = pageRegistry[leaf.name];
      return {
        path: leaf.path,
        name: leaf.name,
        component: leafReg.component,
        meta: {
          title: leaf.title != null ? leaf.title : leaf.name,
          icon: leaf.icon != null ? leaf.icon : ''
        },
        sort: leaf.sort != null ? leaf.sort : 0,
        hidden: leaf.hidden != null && leaf.hidden !== 0
      };
    });

    if (childRouters.length === 0) continue;

    const firstVisible = childRouters.find(c => !c.hidden);
    const redirectPath = firstVisible ? basePath + '/' + firstVisible.path : basePath;

    routers.push({
      path: basePath,
      component: Layout,
      redirect: redirectPath,
      name: node.name,
      meta,
      sort,
      hidden,
      children: childRouters
    });
  }
  return routers;
}

function compare(p) {
  return function(m, n) {
    const a = m[p];
    const b = n[p];
    return (b != null ? b : 0) - (a != null ? a : 0);
  };
}

function sortRouters(routers) {
  routers.forEach(r => {
    if (r.children && r.children.length > 0) {
      r.children.sort(compare('sort'));
    }
  });
  routers.sort(compare('sort'));
}

const permission = {
  state: {
    routers: constantRouterMap,
    addRouters: []
  },
  mutations: {
    SET_ROUTERS(state, routers) {
      state.addRouters = routers;
      state.routers = constantRouterMap.concat(routers);
    }
  },
  actions: {
    GenerateRoutes({ commit }, data) {
      return new Promise(resolve => {
        const { menus } = data;
        if (!menus || !menus.length) {
          commit('SET_ROUTERS', []);
          resolve();
          return;
        }
        const menuTree = buildMenuTree(menus);
        const accessedRouters = buildRoutersFromMenuTree(menuTree);
        sortRouters(accessedRouters);
        accessedRouters.push({ path: '*', redirect: '/404', hidden: true });
        commit('SET_ROUTERS', accessedRouters);
        resolve();
      });
    }
  }
};

export default permission;



// WEBPACK FOOTER //
// ./src/store/modules/permission.js